import { useMutation } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import {
  AiFilterInput,
  AiFilterFragmentFragment,
} from '@/__generated__/graphql';
import { gql } from '@/__generated__/index';
import { AIFilterFragment } from '@/routes/_app.p.news-deck/fragments';

const ADD_AI_FILTER = gql(`
  mutation AddAIFilter($input: AIFilterInput!) {
    addAIFilter(input: $input) {
      ...AIFilterFragment
    }
  }
`);

export const useCreateAiFilter = (
  onCompleted?: (filter: AiFilterFragmentFragment) => void,
) => {
  const toast = useToast();
  const [addAiFilter, { loading }] = useMutation<
    { addAIFilter: AiFilterFragmentFragment },
    { input: AiFilterInput }
  >(ADD_AI_FILTER, {
    onCompleted(data) {
      toast.success({
        title: 'AI filter created',
        subTitle: data.addAIFilter.name,
      });
      onCompleted?.(data.addAIFilter);
    },
    onError(error) {
      toast.error({
        title: 'Error creating AI filter',
        subTitle: error?.message || 'Please try again',
      });
    },
    update(cache, { data }) {
      const newAIFilter = data?.addAIFilter;
      if (newAIFilter) {
        cache.modify({
          fields: {
            aiFilters(filters) {
              const newFilterRef = cache.writeFragment({
                fragment: AIFilterFragment,
                fragmentName: 'AIFilterFragment',
                data: newAIFilter,
              });

              if (newFilterRef) {
                return [newFilterRef, ...filters];
              }

              return filters;
            },
          },
        });
      }
    },
  });

  const createFilter = async (input: AiFilterInput): Promise<boolean> => {
    const result = await addAiFilter({
      variables: {
        input,
      },
    });
    return !!result.data?.addAIFilter;
  };

  return { createFilter, loading };
};
